import dayjs from "dayjs";
import { Duration } from "../utils/dayjs";
import {
  AppointmentChannelEnum,
  AppointmentLogChannelEnum,
  AppointmentLogStatusEnum,
  AppointmentLogTypeEnum,
  AppointmentNotificationTypeEnum,
  AppointmentWebhookNotificationTypeEnum,
} from "../core-ui.enums";
import { ChatModeEnum } from "@auvious/integrations";

export interface IAppointmentQueryParams {
  organizerId?: string[];
  duration?: Duration[];
  range?: [Date, Date];
  pageSize?: number;
  pageNumber?: number;
  sortOrder?: "asc" | "desc";
  sortField?: string;
}

export interface IAppointmentParticipantBase {
  id: string;
  type: "CUSTOMER" | "AGENT" | "CLIENT_SERVICE";
}

export interface IAppointmentParticipantTicket {
  type: "ROOM_ACCESS" | "APPOINTMENT_MANAGE";
  url: string;
  expiresAt: string;
}

export interface IAppointmentParticipant extends IAppointmentParticipantBase {
  /** @deprecated use tickets[] instead */
  ticket?: IAppointmentTicket;
  tickets?: IAppointmentParticipantTicket[];
  state?: "UNSET" | "JOINED" | "LEFT" | "WAITING";
  detailsAvailable?: boolean;
}

export interface IAppointmentParticipantOriginator
  extends IAppointmentParticipantBase {
  timestamp: string;
}

export interface IAppointmentInteraction<T extends IAppointmentRouting> {
  customFields?: { [key: string]: string };
  routing: T;
  state?: AppointmentState;
  initiatedBy?: IAppointmentParticipantOriginator;
  errorData?: {
    systemMessage: string;
    userMessage: string;
  };
}

export enum AppointmentRoutingTypeEnum {
  GENESYS_CLOUD_FLOW = "GENESYS_CLOUD_FLOW",
  GENESYS_CLOUD_QUEUE = "GENESYS_CLOUD_QUEUE",
  TALKDESK_DIGITAL_CONNECT = "TALKDESK_DIGITAL_CONNECT",
}

export type AppointmentViewState =
  | "PENDING" // appointment pending
  | "EXPIRED" // appointment is pending AND end time passed
  | "MISSED" // appointment in progress AND customer left AND agent not joined AND end time passed
  | "ONGOING" // appointment in progress AND now is between start and end
  | "OVERRUNING" // appointment in progress AND end time passed AND customer not left
  | "COMPLETED" // appointment complete
  | "FAILED"; // failed, customer never joined

export type AppointmentState =
  | "ACTIVATED"
  | "STARTING"
  | "IN_PROGRESS"
  | "PENDING"
  | "COMPLETE"
  | "EXPIRED"
  | "FAILED"
  | "CANCELLED";

export enum AppointmentParticipantStateEnum {
  WAITING = "WAITING",
  JOINED = "JOINED",
  LEFT = "LEFT",
}

export type AppointmentEventType =
  | "APScheduledEvent"
  | "APRescheduledEvent"
  | "APReassignedEvent"
  | "APUpdatedEvent"
  | "APInProgressEvent"
  | "APCompletedEvent"
  | "APExpiredEvent"
  | "APAgentJoinedEvent"
  | "APAgentLeftEvent"
  | "APCustomerWaitingEvent"
  | "APCustomerJoinedEvent"
  | "APCustomerLeftEvent"
  | "APDeletedEvent"
  | "APFailedEvent"
  | "APParticipantFailedEvent"
  | "APCancelledEvent";

// export enum AppointmentEventTypeEnum {
//   APScheduledEvent = "APScheduledEvent",
//   APRescheduledEvent = "APRescheduledEvent",
//   APReassignedEvent = "APReassignedEvent",
//   APUpdatedEvent = "APUpdatedEvent",
//   APInProgressEvent = "APInProgressEvent",
//   APCompletedEvent = "APCompletedEvent",
//   APExpiredEvent = "APExpiredEvent",
//   APAgentJoinedEvent = "APAgentJoinedEvent",
//   APAgentLeftEvent = "APAgentLeftEvent",
//   APCustomerWaitingEvent = "APCustomerWaitingEvent",
//   APCustomerJoinedEvent = "APCustomerJoinedEvent",
//   APCustomerLeftEvent = "APCustomerLeftEvent",
//   APDeletedEvent = "APDeletedEvent",
// }

export interface IAppointmentRouting {
  type?: AppointmentRoutingTypeEnum;
  properties?: { [key: string]: string };
}

export interface IAppointmentRoutingGenesysCloudFlow
  extends IAppointmentRouting {
  properties: {
    // wiget deployment id with a custom flow to route calls to specific agents
    deploymentId?: string;
    pcEnvironment?: string;
    chatMode?: ChatModeEnum;
    // | "genesys-api"
    // | "genesys-cloud"
    // | "genesys-premise"
    // | "genesys-web-messaging"
    // | "talkdesk-digital-connect";
    //---
    //returned only after appointment is activated
    //Agent User id that will receive the chat interaction. Must map it to architect flow
    agentId?: string;
    ticketId?: string;
    conferenceId?: string;
  };
}

export interface IAppointmentRoutingTalkdeskDC extends IAppointmentRouting {
  properties: {
    touchpointId: string;
    chatMode?: ChatModeEnum;
    talkdeskAgentId?: string;
    //---
    // returned only after appointment is activated
    ticketId?: string;
    conferenceId?: string;
  };
}

export interface IAppointmentRoutingGenesysCloudQueue
  extends IAppointmentRouting {
  properties: {
    deploymentId?: string;
    queueName: string;
    pcEnvironment?: string;
    chatMode?: string;
    //---
    //returned only after appointment is activated
    conferenceId?: string;
    ticketId?: string;
  };
}

export interface IAppointmentTicket {
  accessUrl: string;
  expiresAt: string;
}

export interface IContactBase {
  contact?: {
    primaryEmail: string;
    primaryPhone: string;
  };
  name?: {
    firstName: string;
    lastName: string;
  };
  username?: string;
}

export interface IContact extends IContactBase {
  id: string;
  organizationId: string;
  extra?: {
    height?: string;
    personality?: string;
    weight?: string;
    "frequently-used-words"?: string[];
  };
}

export interface IAppointmentContact extends IContactBase {
  metadata?: { [key: string]: any };
  ticket?: IAppointmentTicket;
  tickets?: IAppointmentParticipantTicket[];
}

export const DEFAULT_APPOINTMENT_CONTACT: IAppointmentContact = {
  contact: {
    primaryEmail: undefined,
    primaryPhone: undefined,
  },
  metadata: {},
  name: {
    firstName: undefined,
    lastName: undefined,
  },
  username: undefined,
};

export interface IScheduledAppointmentContact extends IAppointmentContact {
  id: string;
  type: "CUSTOMER" | "AGENT";
}

interface IAppointmentOptions<T extends IAppointmentRouting> {
  title?: string;
  notes?: string;
  metadata?: IAppointmentMetadata;
  notifications?: IAppointmentNotification[];
  interaction?: IAppointmentInteraction<T>;
  notificationHeaders?: { [key: string]: string };
}

interface IAppointmentBase<T extends IAppointmentRouting>
  extends IAppointmentOptions<T> {
  duration: string;
  start: Date;
  timezone: string;
  applicationId: string;
}

/**
 * A slice of a day, as returned by the appointment count API, along with how many
 * appointments already sit in it.
 */
export interface IAppointmentTimeFrameCount {
  start: string;
  end: string;
  timezone: string;
  /** ISO-8601 duration of the time frame */
  duration: string;
  count: number;
}

export interface IAppointmentMetadata {
  timezone?: string;
  //@deprecated. use interaction.routing.properties.chatMode instead
  chatMode?: string;
}

export interface IAppointment<T extends IAppointmentRouting>
  extends IAppointmentBase<T> {
  id?: string;
  end: Date;
  organizer: IAppointmentParticipant;
  participants: IAppointmentParticipant[];
  targetAssignment: { id: string; type: "QUEUE" | "AGENT" };
}

export interface IScheduleAppointmentRequest
  extends IAppointmentBase<IAppointmentRouting> {
  customers?: IAppointmentContact[];
  locale?: string;
  targetAssignment?: { id: string; type: "QUEUE" | "AGENT" };
}

export interface IScheduleAppointmentOptions
  extends IAppointmentOptions<IAppointmentRouting> {
  id: string;
}

export interface IAppointmentNotification {
  channel: AppointmentChannelEnum;
  type:
    | AppointmentNotificationTypeEnum
    | AppointmentWebhookNotificationTypeEnum;
  period?: string;
  message?: string; // support previous model
  textBody?: string;
  htmlBody?: string;
  subject?: string;
  enabled?: boolean;
  properties?: string[];
  id: string;
}

export interface IAppointmentFormat {
  example: string;
  format: string;
}

export interface IAppointmentLocale {
  locale: string;
  description: string;
}

export interface IAppointmentNotificationRecipient {
  displayName: string; // DisplayName is a human-friendly label of the recipient; when not available, mapping sets "-"
  reasonCode: string; // RecipientReasonCode indicates why the recipient could not be resolved when displayName is "-"
  type: string; // RecipientType enumerates allowed recipient types
}

export interface IAppointmentLog {
  id: string;
  appointmentId: string;
  errorMessage: string;
  message: string;
  offset: string;
  recipient: IAppointmentNotificationRecipient;
  retryCount: number;
  status: AppointmentLogStatusEnum;
  type: AppointmentLogTypeEnum;
  channel: AppointmentLogChannelEnum;
}

export interface IAppointmentNotificationLog extends IAppointmentLog {
  canRetry?: boolean;
  providerStatus: string;
  createdAt: string;
  updatedAt: string;
  scheduledFor?: string;
}

export interface IAppointmentNotificationLogEvent extends IAppointmentLog {
  conversationId: string;
  messageId: string;
  pollAttempts: number;
  providerStatus: string;
  statusId: string;
  timestamp: string;
  
}
